<template>
  <div v-if="isLoggedIn" class="tw-space-y-4">
    <div class="tw-flex tw-items-center tw-space-x-4">
      <div
        v-if="!isScrubbed && !modal"
        class="tw-flex tw-items-center tw-space-x-1"
      >
        <tooltip-hover :text="$t('good_karma')">
          <font-awesome-icon
            v-if="isOwner"
            icon="thumbs-up"
            class="tw-mr-1 tw-opacity-40"
          />
          <base-button
            v-else
            success
            auto-width
            no-padding
            :text-link="karmaGiven === 1"
            :text-link-hover="karmaGiven !== 1"
            :disabled="isOwner"
            icon="thumbs-up"
            @click="updateKarma(1)"
          />
        </tooltip-hover>
        <span class="tw-font-bold tw-text-sm">
          {{ humanNumberFormat(karma) }}
        </span>
        <tooltip-hover v-if="canDownvote" :text="$t('bad_karma')">
          <font-awesome-icon
            v-if="isOwner"
            icon="thumbs-down"
            class="tw-ml-1 tw-opacity-40"
          />
          <base-button
            v-else
            danger
            auto-width
            no-padding
            :text-link="karmaGiven === -1"
            :text-link-hover="karmaGiven !== -1"
            :disabled="isOwner"
            icon="thumbs-down"
            @click="updateKarma(-1)"
          />
        </tooltip-hover>
      </div>

      <base-button
        v-if="showForm"
        text-link-hover
        small
        no-padding
        class="tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100"
        @click="cancelCloseForm"
      >
        {{ $t('cancel') }}
      </base-button>
      <template v-else>
        <component
          :is="canEditReply ? 'div' : 'tooltip-hover'"
          v-if="level < MAX_DISCUSSION_LEVEL && !isScrubbed"
          :text="$t('comment_cant_reply_locked')"
          background="warning"
          position="right"
        >
          <base-button
            text-link-hover
            small
            no-padding
            :class="{
              'tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100':
                canEditReply,
            }"
            :disabled="!canEditReply"
            @click="openReplyForm"
          >
            {{ $t('reply') }}
          </base-button>
        </component>

        <base-button
          v-if="modal && !read"
          text-link-hover
          small
          no-padding
          :class="{
            'tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100':
              canEditReply,
          }"
          :disabled="!canEditReply"
          @click="$emit('read')"
        >
          {{ $t('mark_as_read') }}
        </base-button>

        <component
          :is="canEditReply ? 'div' : 'tooltip-hover'"
          v-if="isOwner && !isScrubbed && !modal"
          :text="$t('comment_cant_edit_locked')"
          background="warning"
          position="right"
        >
          <base-button
            text-link-hover
            small
            no-padding
            :class="{
              'tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100':
                canEditReply,
            }"
            :disabled="!canEditReply"
            @click="startEditingComment"
          >
            {{ $t('edit') }}
          </base-button>
        </component>

        <template v-if="level === 1 && canUpdateOptions">
          <component
            :is="maxPinLimitReached && !isPinned ? 'tooltip-hover' : 'div'"
            v-if="!isScrubbed || isPinned"
            :text="$t('comment_max_pin_limit')"
            background="warning"
          >
            <base-button
              text-link-hover
              small
              no-padding
              :class="{
                'tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100':
                  !maxPinLimitReached || isPinned,
              }"
              :disabled="maxPinLimitReached && !isPinned"
              @click="togglePin"
            >
              {{ isPinned ? $t('unpin') : $t('pin') }}
            </base-button>
          </component>

          <base-button
            v-if="!isScrubbed"
            text-link-hover
            small
            no-padding
            class="tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100"
            @click="toggleLock"
          >
            {{ isLocked ? $t('unlock') : $t('lock') }}
          </base-button>
        </template>
      </template>

      <base-button
        v-if="canDelete && (!isScrubbed || canHardDelete) && !modal"
        text-link-hover
        small
        no-padding
        class="tw-opacity-50 hover:tw-opacity-100 focus:tw-opacity-100"
        @click="toggleModal"
      >
        {{ $t('delete') }}
      </base-button>
    </div>

    <div v-if="showForm" ref="target">
      <slot :toggle="cancelCloseForm" />
    </div>
  </div>

  <delete-modal
    danger
    :show="showModal"
    :status="status"
    :heading-text="`${$t('delete')} ${$t('comment')}`"
    :confirm-text="$t('comment_confirm_delete')"
    @close:modal="toggleModal"
    @delete="deleteComment"
  />
</template>

<script>
import { MAX_PINNED_COMMENT_LIMIT, MAX_DISCUSSION_LEVEL } from '@config'
import { MOD_COMMENT_ALREADY_RATED } from '@shared/js/config/errors.js'
import { discussionStore, karmaStore, authStore } from '@stores'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { computed, nextTick, toRefs, inject, ref } from 'vue'
import DeleteModal from '@components/Modals/DeleteModal.vue'
import { useI18n } from 'vue-i18n'
import {
  DISCUSSION_TYPE_COLLECTIONS,
  DISCUSSION_TYPE_GUIDES,
  DISCUSSION_TYPE_MODS,
  DISCUSSION_TYPES,
  COMMENT_SCRUBBED,
  COMMENT_PINNED,
  COMMENT_LOCKED,
} from '@config/options.js'
import {
  discussionOptionsRequest,
  deleteDiscussionRequest,
  karmaDiscussionRequest,
} from '@services'
import {
  humanNumberFormat,
  apiErrorsToArray,
  unsetBits,
  setBits,
} from '@helpers/utils.js'
import {
  isCommentScrubbed,
  isCommentPinned,
  isCommentLocked,
} from '@helpers/bitHelper.js'
import {
  usePermissions,
  useToggle,
  useStatus,
  useAsync,
  addToast,
} from '@composables'

export default {
  components: {
    TooltipHover,
    DeleteModal,
  },
  props: {
    type: {
      validator: (val) => DISCUSSION_TYPES.includes(val),
      required: true,
    },
    gameNameId: {
      type: String,
      required: true,
    },
    resourceNameId: {
      type: String,
      required: true,
    },
    commentId: {
      type: Number,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
    karma: {
      type: Number,
      default: 0,
    },
    options: {
      type: Number,
      default: 0,
    },
    hasReplies: {
      type: Boolean,
      default: false,
    },
    canDownvote: {
      type: Boolean,
      default: true,
    },
    modal: {
      type: Boolean,
      default: false,
    },
    read: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['read'],
  setup(props) {
    const {
      resourceNameId,
      gameNameId,
      hasReplies,
      commentId,
      isOwner,
      options,
      karma,
      modal,
      type,
    } = toRefs(props)
    const target = ref(null)
    const { isLoggedIn } = authStore()
    const { karma: myKarma, addKarma } = karmaStore()
    const { t } = useI18n()
    const {
      hasPermissionForGuideComputed,
      hasPermissionForCollection,
      hasPermissionForGame,
      hasPermissionForMod,
    } = usePermissions()

    const {
      getTopLevelComment,
      removeDiscussion,
      updateDiscussion,
      getDiscussions,
    } = discussionStore(type.value)

    const {
      toggle: toggleForm,
      close: closeForm,
      show: showForm,
      open: openForm,
    } = useToggle()
    const {
      toggle: toggleModal,
      close: closeModal,
      show: showModal,
    } = useToggle()

    const editId = inject('editId')
    const updateEditId = inject('updateEditId')

    const editing = computed(() => editId.value === commentId.value)

    const canUpdateOptions = computed(() => {
      switch (type.value) {
        case DISCUSSION_TYPE_MODS:
          return hasPermissionForMod(
            resourceNameId.value,
            gameNameId.value,
            'manager'
          )
        case DISCUSSION_TYPE_GUIDES:
          return hasPermissionForGuideComputed(
            gameNameId.value,
            resourceNameId.value
          ).value
        case DISCUSSION_TYPE_COLLECTIONS:
          return hasPermissionForCollection(
            resourceNameId.value,
            gameNameId.value
          )
        default:
          return false
      }
    })

    const canHardDelete = computed(
      () =>
        hasPermissionForGame(gameNameId.value, 'moderator') || !hasReplies.value
    )

    const isPinned = computed(() => isCommentPinned(options.value))
    const isLocked = computed(() => isCommentLocked(options.value))
    const isScrubbed = computed(() => isCommentScrubbed(options.value))
    const canDelete = computed(() => isOwner.value || canUpdateOptions.value)
    const canEditReply = computed(() => {
      if (canUpdateOptions.value || modal.value) return true

      const isTopLevelCommentLocked = isCommentLocked(
        getTopLevelComment(
          gameNameId.value,
          resourceNameId.value,
          commentId.value
        )?.options ?? 0
      )

      return !isTopLevelCommentLocked
    })

    const maxPinLimitReached = computed(
      () =>
        getDiscussions(gameNameId.value, resourceNameId.value).value.filter(
          (comment) => isCommentPinned(comment.options)
        ).length >= MAX_PINNED_COMMENT_LIMIT
    )

    const karmaKey = `${gameNameId.value}/${type.value}/${resourceNameId.value}/${commentId.value}`
    const karmaGiven = computed(() => myKarma.value[karmaKey])

    const { error: errorOptions, run: runOptions } = useAsync(
      (_options) =>
        discussionOptionsRequest(
          gameNameId.value,
          resourceNameId.value,
          type.value,
          commentId.value,
          _options
        ),
      t('comment_error_update')
    )

    const {
      errorRef: errorRefKarma,
      error: errorKarma,
      run: runKarma,
    } = useAsync((update) =>
      karmaDiscussionRequest(
        gameNameId.value,
        resourceNameId.value,
        type.value,
        commentId.value,
        update
      )
    )

    const {
      loading: loadingDelete,
      error: errorDelete,
      run: runDelete,
    } = useAsync(
      () =>
        deleteDiscussionRequest(
          gameNameId.value,
          resourceNameId.value,
          type.value,
          commentId.value
        ),
      t('comment_error_delete')
    )

    const { status } = useStatus({
      loading: loadingDelete,
      error: errorDelete,
    })

    async function deleteComment() {
      await runDelete()

      if (!errorDelete.value) {
        if (canHardDelete.value) {
          removeDiscussion(
            gameNameId.value,
            resourceNameId.value,
            commentId.value
          )
        } else {
          // Scrub comments
          const newOption = setBits(
            options.value,
            COMMENT_LOCKED | COMMENT_SCRUBBED
          )
          updateDiscussion(
            gameNameId.value,
            resourceNameId.value,
            commentId.value,
            { options: newOption, content: '[deleted]' }
          )
        }
        addToast({
          title: t('comment_deleted'),
          isSuccess: true,
        })
        closeModal()
      }
    }

    async function updateKarma(val) {
      const myCurrentKarma = karmaGiven.value
      if (myCurrentKarma === val) {
        return
      }

      const currentKarma = karma.value
      // If user has already submitted opposing karma
      // then we need to add/subtract double the value
      const newKarma = currentKarma + val * (myCurrentKarma + val === 0 ? 2 : 1)

      addKarma(karmaKey, val)
      updateDiscussion(
        gameNameId.value,
        resourceNameId.value,
        commentId.value,
        { karma: newKarma }
      )

      await runKarma(val)

      if (errorKarma.value) {
        // Undo
        updateDiscussion(
          gameNameId.value,
          resourceNameId.value,
          commentId.value,
          { karma: currentKarma }
        )

        if (errorRefKarma.value !== MOD_COMMENT_ALREADY_RATED) {
          addKarma(karmaKey, myCurrentKarma)
          addToast({
            title: t('comment_error_rate'),
            isError: true,
            text: apiErrorsToArray(errorKarma.value).join(' '),
          })
        }
      }
    }

    function startEditingComment() {
      if (!canEditReply.value) return

      updateEditId(commentId.value)
      toggleForm()
    }

    function cancelCloseForm() {
      if (editing.value) {
        updateEditId(null)
      }
      closeForm()
    }

    function openReplyForm() {
      if (!canEditReply.value) return

      updateEditId(null)
      openForm()
      nextTick(() => {
        // Focus textarea after it's mounted
        target.value.querySelector('textarea')?.focus()
        target.value?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      })
    }

    async function togglePin() {
      const newOption = (isPinned.value ? unsetBits : setBits)(
        options.value,
        COMMENT_PINNED
      )
      _updateOptions(newOption)
    }

    function toggleLock() {
      const newOption = (isLocked.value ? unsetBits : setBits)(
        options.value,
        COMMENT_LOCKED
      )
      _updateOptions(newOption)
    }

    async function _updateOptions(newOptions) {
      const oldOptions = options.value

      updateDiscussion(
        gameNameId.value,
        resourceNameId.value,
        commentId.value,
        { options: newOptions }
      )

      await runOptions(newOptions)

      if (errorOptions.value) {
        updateDiscussion(
          gameNameId.value,
          resourceNameId.value,
          commentId.value,
          { options: oldOptions }
        )
      }
    }

    return {
      MAX_DISCUSSION_LEVEL,
      startEditingComment,
      maxPinLimitReached,
      humanNumberFormat,
      canUpdateOptions,
      cancelCloseForm,
      deleteComment,
      openReplyForm,
      canHardDelete,
      canEditReply,
      toggleModal,
      updateKarma,
      isScrubbed,
      karmaGiven,
      isLoggedIn,
      toggleLock,
      canDelete,
      showModal,
      togglePin,
      showForm,
      isPinned,
      isLocked,
      target,
      status,
    }
  },
}
</script>
